import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, Check, X, Settings } from 'lucide-react';
import { Button } from './ui/button';

export default function CookieConsent() {
  const { i18n } = useTranslation();
  const [show, setShow] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false
  });

  const lang = i18n.language;

  const content = {
    en: {
      title: "Cookie Settings",
      description: "We use cookies to enhance your experience. Essential cookies are required for the site to function. Analytics cookies help us improve our service.",
      acceptAll: "Accept All",
      acceptSelected: "Save Preferences",
      rejectOptional: "Essential Only",
      settings: "Cookie Settings",
      essential: "Essential Cookies",
      essentialDesc: "Required for the website to function properly (authentication, security, preferences)",
      analytics: "Analytics Cookies",
      analyticsDesc: "Help us understand how visitors interact with our website",
      marketing: "Marketing Cookies",
      marketingDesc: "Used to deliver relevant advertisements",
      learnMore: "Learn more in our"
    },
    nl: {
      title: "Cookie-instellingen",
      description: "Wij gebruiken cookies om uw ervaring te verbeteren. Essentiële cookies zijn vereist voor de werking van de site. Analytische cookies helpen ons onze service te verbeteren.",
      acceptAll: "Alles Accepteren",
      acceptSelected: "Voorkeuren Opslaan",
      rejectOptional: "Alleen Essentieel",
      settings: "Cookie-instellingen",
      essential: "Essentiële Cookies",
      essentialDesc: "Vereist voor de goede werking van de website (authenticatie, beveiliging, voorkeuren)",
      analytics: "Analytische Cookies",
      analyticsDesc: "Helpen ons begrijpen hoe bezoekers onze website gebruiken",
      marketing: "Marketing Cookies",
      marketingDesc: "Gebruikt om relevante advertenties te tonen",
      learnMore: "Meer informatie in ons"
    },
    de: {
      title: "Cookie-Einstellungen",
      description: "Wir verwenden Cookies, um Ihre Erfahrung zu verbessern.",
      acceptAll: "Alle akzeptieren",
      acceptSelected: "Einstellungen speichern",
      rejectOptional: "Nur Essentiell",
      settings: "Cookie-Einstellungen",
      essential: "Essentielle Cookies",
      essentialDesc: "Erforderlich für die Funktion der Website",
      analytics: "Analyse-Cookies",
      analyticsDesc: "Helfen uns zu verstehen, wie Besucher unsere Website nutzen",
      marketing: "Marketing-Cookies",
      marketingDesc: "Für relevante Werbung verwendet",
      learnMore: "Mehr erfahren in unserer"
    },
    fr: {
      title: "Paramètres des cookies",
      description: "Nous utilisons des cookies pour améliorer votre expérience.",
      acceptAll: "Tout accepter",
      acceptSelected: "Enregistrer les préférences",
      rejectOptional: "Essentiels uniquement",
      settings: "Paramètres",
      essential: "Cookies essentiels",
      essentialDesc: "Nécessaires au fonctionnement du site",
      analytics: "Cookies analytiques",
      analyticsDesc: "Nous aident à comprendre comment les visiteurs utilisent notre site",
      marketing: "Cookies marketing",
      marketingDesc: "Utilisés pour afficher des publicités pertinentes",
      learnMore: "En savoir plus dans notre"
    },
    es: {
      title: "Configuración de cookies",
      description: "Utilizamos cookies para mejorar su experiencia.",
      acceptAll: "Aceptar todo",
      acceptSelected: "Guardar preferencias",
      rejectOptional: "Solo esenciales",
      settings: "Configuración",
      essential: "Cookies esenciales",
      essentialDesc: "Necesarias para el funcionamiento del sitio",
      analytics: "Cookies analíticas",
      analyticsDesc: "Nos ayudan a entender cómo los visitantes usan nuestro sitio",
      marketing: "Cookies de marketing",
      marketingDesc: "Utilizadas para mostrar anuncios relevantes",
      learnMore: "Más información en nuestra"
    },
    it: {
      title: "Impostazioni cookie",
      description: "Utilizziamo i cookie per migliorare la tua esperienza.",
      acceptAll: "Accetta tutto",
      acceptSelected: "Salva preferenze",
      rejectOptional: "Solo essenziali",
      settings: "Impostazioni",
      essential: "Cookie essenziali",
      essentialDesc: "Necessari per il funzionamento del sito",
      analytics: "Cookie analitici",
      analyticsDesc: "Ci aiutano a capire come i visitatori usano il nostro sito",
      marketing: "Cookie di marketing",
      marketingDesc: "Utilizzati per mostrare annunci pertinenti",
      learnMore: "Scopri di più nella nostra"
    },
    ru: {
      title: "Настройки файлов cookie",
      description: "Мы используем файлы cookie для улучшения вашего опыта.",
      acceptAll: "Принять все",
      acceptSelected: "Сохранить настройки",
      rejectOptional: "Только необходимые",
      settings: "Настройки",
      essential: "Необходимые cookie",
      essentialDesc: "Требуются для работы сайта",
      analytics: "Аналитические cookie",
      analyticsDesc: "Помогают понять, как посетители используют наш сайт",
      marketing: "Маркетинговые cookie",
      marketingDesc: "Используются для показа релевантной рекламы",
      learnMore: "Подробнее в нашей"
    },
    ar: {
      title: "إعدادات ملفات تعريف الارتباط",
      description: "نستخدم ملفات تعريف الارتباط لتحسين تجربتك.",
      acceptAll: "قبول الكل",
      acceptSelected: "حفظ التفضيلات",
      rejectOptional: "الأساسية فقط",
      settings: "الإعدادات",
      essential: "ملفات تعريف الارتباط الأساسية",
      essentialDesc: "مطلوبة لعمل الموقع",
      analytics: "ملفات تعريف الارتباط التحليلية",
      analyticsDesc: "تساعدنا على فهم كيفية استخدام الزوار لموقعنا",
      marketing: "ملفات تعريف الارتباط التسويقية",
      marketingDesc: "تستخدم لعرض الإعلانات ذات الصلة",
      learnMore: "اعرف المزيد في"
    }
  };

  const t = content[lang] || content.en;

  useEffect(() => {
    // Check if consent already given
    const consent = localStorage.getItem('cookie-consent');
    if (consent) {
      setShow(false);
      return;
    }
    
    const ageVerified = localStorage.getItem('ella-gold-age-verified');
    
    // Only show cookie banner AFTER age verification is done
    const checkAgeVerified = () => {
      const verified = localStorage.getItem('ella-gold-age-verified');
      const consentGiven = localStorage.getItem('cookie-consent');
      if (verified && !consentGiven) {
        setShow(true);
      }
    };
    
    if (ageVerified) {
      // Age already verified, show cookie banner after short delay
      const timer = setTimeout(() => {
        if (!localStorage.getItem('cookie-consent')) {
          setShow(true);
        }
      }, 500);
      return () => clearTimeout(timer);
    } else {
      // Listen for age verification
      const timer = setInterval(checkAgeVerified, 1000);
      return () => clearInterval(timer);
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem('cookie-consent', JSON.stringify(prefs));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    // Force immediate hide
    setShow(false);
    setShowSettings(false);
    // Force re-render to ensure it's hidden
    window.dispatchEvent(new Event('cookie-consent-saved'));
  };

  const handleAcceptAll = () => {
    saveConsent({ essential: true, analytics: true, marketing: true });
  };

  const handleRejectOptional = () => {
    saveConsent({ essential: true, analytics: false, marketing: false });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto bg-[#0A0A0A] border border-white/20 rounded-xl shadow-2xl overflow-hidden">
        {!showSettings ? (
          // Main banner
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#D4AF37]/20 rounded-full flex-shrink-0">
                <Cookie className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-2">{t.title}</h3>
                <p className="text-gray-400 text-sm mb-4">
                  {t.description}{' '}
                  <a href="/privacy" className="text-[#D4AF37] hover:underline">
                    {t.learnMore} Privacy Policy
                  </a>
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={handleAcceptAll}
                    className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                    data-testid="accept-all-cookies"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {t.acceptAll}
                  </Button>
                  <Button
                    onClick={handleRejectOptional}
                    variant="outline"
                    className="border-white/20"
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t.rejectOptional}
                  </Button>
                  <Button
                    onClick={() => setShowSettings(true)}
                    variant="ghost"
                    className="text-gray-400 hover:text-white"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {t.settings}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Settings panel
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#D4AF37]" />
                {t.settings}
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-white/10 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {/* Essential - always on */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <h4 className="font-medium">{t.essential}</h4>
                  <p className="text-sm text-gray-400">{t.essentialDesc}</p>
                </div>
                <div className="w-12 h-6 bg-[#D4AF37] rounded-full flex items-center justify-end px-1">
                  <div className="w-4 h-4 bg-white rounded-full" />
                </div>
              </div>

              {/* Analytics */}
              <label className="flex items-center justify-between p-4 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10">
                <div>
                  <h4 className="font-medium">{t.analytics}</h4>
                  <p className="text-sm text-gray-400">{t.analyticsDesc}</p>
                </div>
                <div 
                  className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                    preferences.analytics ? 'bg-[#D4AF37] justify-end' : 'bg-gray-600 justify-start'
                  }`}
                  onClick={() => setPreferences({...preferences, analytics: !preferences.analytics})}
                >
                  <div className="w-4 h-4 bg-white rounded-full" />
                </div>
              </label>

              {/* Marketing */}
              <label className="flex items-center justify-between p-4 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10">
                <div>
                  <h4 className="font-medium">{t.marketing}</h4>
                  <p className="text-sm text-gray-400">{t.marketingDesc}</p>
                </div>
                <div 
                  className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                    preferences.marketing ? 'bg-[#D4AF37] justify-end' : 'bg-gray-600 justify-start'
                  }`}
                  onClick={() => setPreferences({...preferences, marketing: !preferences.marketing})}
                >
                  <div className="w-4 h-4 bg-white rounded-full" />
                </div>
              </label>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleSavePreferences}
                className="flex-1 bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {t.acceptSelected}
              </Button>
              <Button
                onClick={handleAcceptAll}
                variant="outline"
                className="flex-1 border-white/20"
              >
                {t.acceptAll}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
